import { useState, useEffect, useCallback } from "react";
import {
  Alert, Box, Button, CircularProgress, Divider,
  InputAdornment, Paper, TextField, Typography, Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";

const STEPS = [500, 1000, 5000, 10000, 50000];

export default function BadBeatJackpot() {
  const token = useSelector((s) => s.loginSlice.token);

  const [currentAmount, setCurrentAmount] = useState(null);
  const [inputAmount, setInputAmount]     = useState("");
  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState("");
  const [success, setSuccess]             = useState("");

  const fetchBBJ = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosRequest("GET", `${apiBaseUrl}/bbj`, token);
      if (res?.data?.status) {
        const amt = res.data.data.bbj_amount;
        setCurrentAmount(amt);
        setInputAmount(String(amt));
      } else {
        setError("Failed to load BBJ amount.");
      }
    } catch {
      setError("Network error while loading BBJ.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBBJ(); }, [fetchBBJ]);

  const handleStep = (delta) => {
    const current = Number(inputAmount) || 0;
    const next = Math.max(0, current + delta);
    setInputAmount(String(next));
    setSuccess("");
    setError("");
  };

  const handleSave = async () => {
    const val = Number(inputAmount);
    if (isNaN(val) || inputAmount.trim() === "") {
      setError("Please enter a valid amount.");
      return;
    }
    if (val < 0) {
      setError("Amount cannot be negative.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const res = await axiosRequest("PUT", `${apiBaseUrl}/bbj`, token, { bbj_amount: val });
      if (res?.data?.status) {
        setCurrentAmount(val);
        setSuccess("BBJ amount updated successfully!");
      } else {
        setError(res?.data?.message || "Failed to update BBJ amount.");
      }
    } catch {
      setError("Network error while saving.");
    } finally {
      setSaving(false);
    }
  };

  const isDirty = Number(inputAmount) !== currentAmount;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8, maxWidth: 640, mx: "auto" }}>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: 0.5 }}>
            Bad Beat Jackpot
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Manage the current jackpot pool amount
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchBBJ}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Current Amount Card */}
      <Paper
        elevation={4}
        sx={{
          p: 4,
          mb: 3,
          borderRadius: 3,
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
          color: "#fff",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -40, right: -40,
            width: 160, height: 160,
            borderRadius: "50%",
            background: "rgba(255,215,0,0.08)",
          },
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.7, letterSpacing: 2, textTransform: "uppercase" }}>
          Current Jackpot Pool
        </Typography>

        {loading ? (
          <Box mt={2}><CircularProgress sx={{ color: "#ffd700" }} /></Box>
        ) : (
          <Typography
            variant="h2"
            fontWeight={900}
            sx={{ color: "#ffd700", mt: 1, letterSpacing: 1, textShadow: "0 0 20px rgba(255,215,0,0.4)" }}
          >
            {currentAmount !== null ? Number(currentAmount).toLocaleString("en-IN") : "—"}
          </Typography>
        )}

        {currentAmount !== null && !loading && (
          <Chip
            label="LIVE"
            size="small"
            sx={{ mt: 2, background: "#ffd700", color: "#1a1a2e", fontWeight: 700, fontSize: 11, letterSpacing: 1 }}
          />
        )}
      </Paper>

      {/* Editor Card */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={2}>
          Adjust Jackpot Amount
        </Typography>

        {/* Quick step buttons */}
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary" mb={1} display="block">
            Quick Decrease
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {STEPS.map((s) => (
              <Button
                key={`-${s}`}
                variant="outlined"
                color="error"
                size="small"
                startIcon={<RemoveIcon />}
                onClick={() => handleStep(-s)}
                sx={{ minWidth: 90, fontWeight: 700 }}
              >
                {s >= 1000 ? `${s / 1000}K` : s}
              </Button>
            ))}
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="caption" color="text.secondary" mb={1} display="block">
            Quick Increase
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {STEPS.map((s) => (
              <Button
                key={`+${s}`}
                variant="outlined"
                color="success"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleStep(s)}
                sx={{ minWidth: 90, fontWeight: 700 }}
              >
                {s >= 1000 ? `${s / 1000}K` : s}
              </Button>
            ))}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Manual input */}
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary" mb={1} display="block">
            Or set exact amount
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="BBJ Amount ()"
            value={inputAmount}
            onChange={(e) => {
              setInputAmount(e.target.value);
              setSuccess("");
              setError("");
            }}
            inputProps={{ min: 0 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"></InputAdornment>,
            }}
          />
          {isDirty && currentAmount !== null && (
            <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              Change: {Number(inputAmount) > currentAmount ? "+" : ""}
              {(Number(inputAmount) - currentAmount).toLocaleString("en-IN")} from current {Number(currentAmount).toLocaleString("en-IN")}
            </Typography>
          )}
        </Box>

        {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading || !isDirty}
          sx={{
            fontWeight: 700,
            fontSize: 16,
            py: 1.5,
            background: "linear-gradient(135deg, #0f3460, #16213e)",
            "&:hover": { background: "linear-gradient(135deg, #16213e, #0f3460)" },
            "&:disabled": { opacity: 0.5 },
          }}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Paper>
    </Box>
  );
}
